import { Location } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ArticleCardOptions } from "../../directives";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { CoreLanguageManager } from "../../services/ILanguageManager";
import { ItemTagHelperService } from "../../services/itemTagHelper.service";
import { CoreLogService } from "../../services/log.service";
import { CouponRedemptionService } from "../../services/couponRedemption.service";
import { RouterHelperService } from "../../services/routerHelper.service";
import { WebShopService } from "../../services/webShop.service";
import { ItemTagStatusType, ItemTagType, TranslationContentType, YourAPPProductType } from "../../shared/contentTypes";
import { ItemTagViewModel, RecipelinkingViewModel } from "../../shared/models";
import { PagingHelper } from "../../shared/pagingHelper";
import Utils from "../../shared/utils";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { RecipeLinkService } from "./recipeLink.service";

@Component({
  selector: 'recipeLink',
  templateUrl: './recipeLink.component.html',
  styleUrls: ['./recipeLink.component.scss'],
  providers: [RecipeLinkService],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class RecipeLinkComponent implements OnInit, OnDestroy {
  public recipeLink: RecipelinkingViewModel;
  public itemTags: ItemTagViewModel[];
  public isItemTagEnabled: boolean = false;
  public itemTagType: ItemTagType;
  public tagType = ItemTagType;
  public ArticleCardConfig: ArticleCardOptions;

  private useBrowserHistory: boolean;
  public activeItemGroupId: number;
  private productId: number;
  private _locationFilterId: number = 0;

  private _recipeChangesSubscriber: Subscription;
  private _viewTypeSubscription: Subscription;
  private _isOpenedFromCoupon: boolean = false;
  private _isCouponLinkedProductChosen: boolean = false;
  /** productId of the most recently chosen linked product; guards revokeCouponFromLinkedProduct against undoing a newer choice when the guest switches before the first navigate settles. */
  private _couponLinkedProductId: number | null = null;

  public isSmallView: boolean = false;
  public hasImage: boolean;
  public loadItems: boolean = false;

  constructor(
    private _recipeLinkService: RecipeLinkService,
    private _webShopService: WebShopService,
    private _location: Location,
    private _routerHelperService: RouterHelperService,
    private _activatedRoute: ActivatedRoute,
    private _coreLogService: CoreLogService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _mainnavService: MainnavService,
    private _itemTagHelperService: ItemTagHelperService,
    private _couponRedemptionService: CouponRedemptionService) {
  }

  ngOnInit() {
    const me = this;
    me.getRouteData();
    me._isOpenedFromCoupon = me._couponRedemptionService.consumeCouponFlowNavigationMarker(me.productId);
    me.setArticleCardConfig();
    me._webShopService.getWebshopSettingsDataModel().then(async (settings) => {
      if (settings.is_itemtag_enabled) {
        me.itemTags = await me._itemTagHelperService.GetProductTags();
        if (me.itemTags && me.itemTags.length > 0) {
          me.isItemTagEnabled = settings.is_itemtag_enabled;
          me.itemTagType = settings.itemtag_type;
        }
      }
      me.reloadDataAndRender();
    });
    me._recipeChangesSubscriber = me._recipeLinkService.ObserveChanges.subscribe(() => {
      me.reloadDataAndRender();
    });

    me.onViewTypeChanges(me._mainnavService.viewType);
    me._viewTypeSubscription = me._mainnavService.observeViewTypeChanges.subscribe((viewType) => {
      me.onViewTypeChanges(viewType);
      me.PushChanges();
    });
    me.PushChanges();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._recipeChangesSubscriber) {
      me._recipeChangesSubscriber.unsubscribe();
    }
    if (me._viewTypeSubscription) {
      me._viewTypeSubscription.unsubscribe();
    }
    if (me._isOpenedFromCoupon && !me._isCouponLinkedProductChosen) {
      me._couponRedemptionService.discardReservedCoupon()
        .catch(error => me._coreLogService.error("RecipeLinkComponent.ngOnDestroy", "discardReservedCoupon", error));
    }
  }

  /** Re-arms the one-shot coupon-flow marker for the chosen linked product right before ArticleCardComponent navigates to it. */
  public passCouponToLinkedProduct(productId: number): void {
    const me = this;
    if (me._isOpenedFromCoupon) {
      me._isCouponLinkedProductChosen = true;
      me._couponLinkedProductId = productId;
      me._couponRedemptionService.markCouponFlowNavigation(productId);
    }
  }

  /** Undoes the re-arm for productId; ignored if a newer variant was already chosen (race guard for a fast switch before this navigate settles). */
  public revokeCouponFromLinkedProduct(productId: number): void {
    const me = this;
    if (me._isOpenedFromCoupon && me._isCouponLinkedProductChosen && productId === me._couponLinkedProductId) {
      me._isCouponLinkedProductChosen = false;
      me._couponRedemptionService.unmarkCouponProduct(productId);
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("RecipeLinkComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  private onViewTypeChanges(viewType: ViewType) {
    const me = this;
    me.isSmallView = (viewType === ViewType.Small);
  }

  public get recipeDescription(): string {
    const _details = InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(this.recipeLink.details_en) ? this.recipeLink.details_de : this.recipeLink.details_en;
    if (YourAPPProductType.sot === environment.producttype) {
      const details = CoreLanguageManager.translate(TranslationContentType.Article_Bontext, this.recipeLink.id, _details);
      return Utils.decodeHTMLEntities(details);
    }
    return Utils.decodeHTMLEntities(_details);
  }

  private setArticleCardConfig() {
    const me = this;
    me.ArticleCardConfig = {
      checkAvailability: true,
      showPrice: true,
      allowAddingToBasket: false,
      allowViewingDetails: true,
    };
  }

  public onImageError() {
    const me = this;
    me.hasImage = false;
    me.PushChanges();
  }

  public svgSanitizer(svg: string, color: string) {
    const me = this;
    return me._itemTagHelperService.svgSanitizer(svg, color);
  }

  private async reloadDataAndRender() {
    const me = this;
    let likeIds: number[] = [];
    let dislikeIds: number[] = [];

    if (me.itemTags && me.itemTags.length > 0) {
      likeIds = me.itemTags.filter(t => t.itemTagStatus === ItemTagStatusType.like).map(m => m.id);
      dislikeIds = me.itemTags.filter(t => t.itemTagStatus === ItemTagStatusType.dislike).map(m => m.id);
    }

    await me._recipeLinkService.GetRecipeLinking(me._locationFilterId, me.productId, me.itemTags, likeIds, dislikeIds).then((recipeLink) => {
      if (recipeLink) {
        if (recipeLink.products.length > 0) {
          me.loadItems = true;
        }
        me.recipeLink = recipeLink;
        me.hasImage = me.recipeLink.hasImage;
      }
      me.PushChanges();
    }).catch(e => {
      me._coreLogService.error("RecipeLinkComponent.ReloadDataAndRender", "GetRecipeLinking", e);
    });
  }

  private getRouteData() {
    const me = this;
    me.productId = +(me._activatedRoute.snapshot.paramMap.get("productId"));
    me._locationFilterId = +(me._activatedRoute.snapshot.data.locationId);
    me.useBrowserHistory = me._activatedRoute.snapshot.data["useBrowserHistory"];
    const routeTargetData = me._activatedRoute.snapshot.data["routeTargetData"];
    if (routeTargetData && routeTargetData["activeItemGroupId"]) {
      me.activeItemGroupId = routeTargetData["activeItemGroupId"];
    } else {
      me.activeItemGroupId = +(me._activatedRoute.snapshot.paramMap.get("activeItemGroupId"));
    }
  }

  public navigateBack(): void {
    const me = this;
    if (me.useBrowserHistory) {
      me._location.back();
    } else {
      me._routerHelperService.setRouteTargetDataByKey("activeItemGroupId", me.activeItemGroupId);
      me._location.back();
    }
  }

  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

}
